// ============================================================================
// RoutineCard — a scheduled routine
// Full form for event 'created' (name, description, mono schedule row with
// Active/Paused dot, next run); receipt form for 'ran' (✓ ring, name +
// run_summary, quiet 'See changes' link). Paused state dims the title.
// ============================================================================

import { Box, Typography } from '@mui/material'
import { beoflowColors, typography, spacing } from '@/theme/muiTheme'
import type { RenderPayload, RoutineSpec } from '@/types/chatRenders'
import CardShell, { cardChromeSx, monoMetaSx } from './CardShell'
import { entityRoute, formatShortDate } from './cardUtils'

export default function RoutineCard({ render }: { render: RenderPayload }) {
  const spec = render.spec as unknown as Partial<RoutineSpec>
  const routine = spec.routine ?? {}
  const name = routine.name ?? routine.title ?? render.entity.title
  const enabled = routine.enabled !== false
  const event = spec.event ?? (render.status === 'created' ? 'created' : undefined)
  const nextRun = formatShortDate(routine.next_run_at)
  const route = entityRoute(render.entity)

  // Receipt form for a run.
  if (event === 'ran') {
    return (
      <Box
        sx={{
          ...cardChromeSx,
          my: spacing.xs,
          px: spacing.md,
          py: spacing.sm,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          minWidth: 0,
        }}
      >
        {/* ✓ ring */}
        <Box
          component="span"
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: `1.5px solid ${beoflowColors.tealBase}`,
            color: beoflowColors.tealBase,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: typography.fontFamilyMonospace,
            fontSize: '9px',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ✓
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '14px', fontWeight: typography.fontWeightMedium }}>
            {name}
          </Typography>
          {spec.run_summary && (
            <Typography noWrap sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {spec.run_summary}
            </Typography>
          )}
        </Box>
        {spec.run_summary && (
          <Typography
            component="span"
            // TODO: deep-link to the run's changes once a target surface exists.
            sx={{
              ...monoMetaSx,
              color: beoflowColors.meanBlue,
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            See changes
          </Typography>
        )}
      </Box>
    )
  }

  // Full form (created / updated / paused).
  return (
    <CardShell kindLabel={`Routine${event && event !== 'created' ? ` · ${event}` : ''}`} action={route ? { label: 'Open', to: route } : undefined} disableBodyPadding>
      <Box sx={{ px: spacing.md, pt: spacing.sm, pb: spacing.sm }}>
        <Typography
          noWrap
          sx={{
            fontSize: '15px',
            fontWeight: typography.fontWeightSemibold,
            ...(enabled ? {} : { color: 'text.secondary' }),
          }}
        >
          {name}
        </Typography>
        {routine.description && (
          <Typography sx={{ fontSize: '13px', lineHeight: 1.55, color: 'text.secondary', mt: '2px' }}>
            {routine.description}
          </Typography>
        )}
      </Box>
      {/* Schedule row under a full-bleed hairline (frames). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
          px: spacing.md,
          py: '9px',
          borderTop: '1px solid rgba(248, 246, 244, 0.09)',
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: enabled ? beoflowColors.tealBase : 'rgba(248, 246, 244, 0.28)',
            flexShrink: 0,
          }}
        />
        <Typography noWrap sx={{ ...monoMetaSx, ...(enabled ? { color: beoflowColors.tealBase } : {}) }}>
          {enabled ? 'Active' : 'Paused'}
        </Typography>
        {routine.schedule && (
          <Typography noWrap sx={{ ...monoMetaSx, minWidth: 0 }}>
            {routine.schedule}
          </Typography>
        )}
        {enabled && nextRun && (
          <Typography noWrap sx={{ ...monoMetaSx, flexShrink: 0 }}>
            Next {nextRun}
          </Typography>
        )}
      </Box>
    </CardShell>
  )
}
